import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  CreateLoginCarouselItemDto,
  ILoginCarouselItem,
  UpdateLoginCarouselItemDto,
} from '~/Services/LoginBranding.interface'
import { ModalName } from './constants'

interface ModalVisibility {
  name: string
  visible: boolean
}

interface LoginBrandingState {
  isLoading: boolean
  isConfirmLoading: boolean
  isImageSaving: boolean
  // Application đang được xem carousel — dùng để chặn hiển thị dữ liệu cũ của 1 application khác khi
  // Edit Application modal đổi sang application khác trước khi request cũ kịp trả về (race condition).
  applicationId?: string
  items: ILoginCarouselItem[]
  itemSelected?: ILoginCarouselItem
  modalVisibility: {
    [id: string]: { visible: boolean }
  }
}

const initialState: LoginBrandingState = {
  isLoading: false,
  isConfirmLoading: false,
  isImageSaving: false,
  items: [],
  modalVisibility: {},
}

export const LoginBrandingSlice = createSlice({
  name: 'loginBranding',
  initialState,
  reducers: {
    getCarouselItems: (state, action: PayloadAction<{ applicationId: string }>) => {
      state.isLoading = true
      state.applicationId = action.payload.applicationId
    },
    getCarouselItemsSuccess: (
      state,
      action: PayloadAction<{ applicationId: string; items: ILoginCarouselItem[] }>,
    ) => {
      // Response của 1 applicationId cũ đã bị đổi khỏi state (user chuyển sang app khác nhanh) -> bỏ qua,
      // không ghi đè list đang hiển thị đúng application hiện tại.
      if (state.applicationId === action.payload.applicationId) {
        state.items = action.payload.items
      }
      state.isLoading = false
    },
    getCarouselItemsFail: state => {
      state.isLoading = false
    },
    resetCarouselItems: state => {
      state.items = []
      state.applicationId = undefined
    },
    setItemSelected: (
      state,
      action: PayloadAction<ILoginCarouselItem | undefined>,
    ) => {
      state.itemSelected = action.payload
    },
    setModalVisibility: (state, action: PayloadAction<ModalVisibility>) => {
      const { name, visible } = action.payload
      state.modalVisibility[name] = { visible }
    },
    createCarouselItem: (
      state,
      // pendingImageFile: ảnh nền chọn sẵn lúc "Add slide" — chưa có itemId để upload ngay lúc chọn
      // (item chưa tồn tại trong DB), nên giữ tạm ở đây, epic sẽ upload nối tiếp ngay sau khi tạo xong.
      action: PayloadAction<{ applicationId: string; data: CreateLoginCarouselItemDto; pendingImageFile?: File }>,
    ) => {
      state.isConfirmLoading = true
    },
    createCarouselItemSuccess: state => {
      state.isConfirmLoading = false
      state.modalVisibility[ModalName.CarouselItem] = { visible: false }
    },
    createCarouselItemFail: state => {
      state.isConfirmLoading = false
    },
    updateCarouselItem: (
      state,
      action: PayloadAction<{ applicationId: string; itemId: number; data: UpdateLoginCarouselItemDto }>,
    ) => {
      state.isConfirmLoading = true
    },
    updateCarouselItemSuccess: state => {
      state.isConfirmLoading = false
      state.modalVisibility[ModalName.CarouselItem] = { visible: false }
    },
    updateCarouselItemFail: state => {
      state.isConfirmLoading = false
    },
    deleteCarouselItem: (
      state,
      action: PayloadAction<{ applicationId: string; itemId: number }>,
    ) => {
      state.isConfirmLoading = true
    },
    deleteCarouselItemSuccess: state => {
      state.isConfirmLoading = false
    },
    deleteCarouselItemFail: state => {
      state.isConfirmLoading = false
    },
    setCarouselItemActive: (
      state,
      action: PayloadAction<{ applicationId: string; itemId: number; isActive: boolean }>,
    ) => {
      state.isConfirmLoading = true
    },
    setCarouselItemDisplayOrder: (
      state,
      action: PayloadAction<{ applicationId: string; itemId: number; displayOrder: number }>,
    ) => {
      state.isConfirmLoading = true
    },
    uploadCarouselItemImage: (
      state,
      action: PayloadAction<{ applicationId: string; itemId: number; file: File }>,
    ) => {
      state.isImageSaving = true
    },
    removeCarouselItemImage: (
      state,
      action: PayloadAction<{ applicationId: string; itemId: number }>,
    ) => {
      state.isImageSaving = true
    },
    // setActive/setDisplayOrder/uploadImage/removeImage đều refetch toàn bộ list sau khi xong (server là
    // nguồn đúng cho DisplayOrder/ordering) thay vì patch từng field tại chỗ — list nhỏ, refetch đơn giản
    // và không lệch thứ tự.
    itemMutationSettled: state => {
      state.isConfirmLoading = false
      state.isImageSaving = false
    },
  },
})

export const LoginBrandingActions = LoginBrandingSlice.actions
export const LoginBrandingReducer = LoginBrandingSlice.reducer
